import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  {
    icon: Phone,
    label: "Phone",
    value: "603-548-9932",
    href: "tel:603-548-9932",
  },
  {
    icon: Mail,
    label: "Email",
    value: "tibstaps@gmail.com",
    href: "mailto:tibstaps@gmail.com",
  },
  {
    icon: MapPin,
    label: "Address",
    value: "Londonderry, NH",
    href: "https://maps.google.com/?q=Londonderry+NH",
  },
];

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formLoadedAt] = useState(() => Date.now() / 1000);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city_state: "",
    business_name: "",
    service_type: "",
    system_type: "",
    number_of_taps: "",
    preferred_contact_time: "",
    message: "",
    website: "",  // honeypot
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city_state: formData.city_state,
        business_name: formData.business_name,
        service_type: formData.service_type || null,
        system_type: formData.system_type || null,
        number_of_taps: formData.number_of_taps ? parseInt(formData.number_of_taps) : null,
        preferred_contact_time: formData.preferred_contact_time || null,
        message: formData.message || null,
        website: formData.website,
        form_loaded_at: formLoadedAt,
      };

      await axios.post(`${API_URL}/leads`, payload);
      
      setSubmitted(true);
      toast.success("Request submitted successfully! We'll be in touch soon.");
      
        // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: "",
          email: "",
          phone: "",
          business_name: "",
          service_type: "",
          system_type: "",
          number_of_taps: "",
          preferred_contact_time: "",
          message: "",
        });
      }, 3000);
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#141414]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div
          className="text-center mb-16"
        >
          <p className="overline mb-4" data-testid="contact-overline">Get In Touch</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="contact-heading">
            Ready to <span className="gold-text">Elevate Your Draft?</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            Request service, ask questions, or schedule a consultation. 
            You'll receive a prompt response from one of our knowledgeable team members.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div
            className="lg:col-span-2 space-y-8"
          >
            <div className="space-y-6">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-4" data-testid={`contact-info-${item.label}`}>
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#A1A1AA] text-sm">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.label === "Address" ? "_blank" : undefined}
                        rel={item.label === "Address" ? "noopener noreferrer" : undefined}
                        className="text-white hover:text-[#D4AF37] transition-colors font-medium"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-white font-medium">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="pt-6 border-t border-[#27272A]">
              <p className="text-[#A1A1AA] text-sm mb-4">Follow Us</p>
              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/TibsTaps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
                  data-testid="social-facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/explore/locations/74093362/tibs-taps/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
                  data-testid="social-instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>

          </div>

          {/* Form */}
          <div
            className="lg:col-span-3"
          >
            <div className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl p-8">
              {submitted ? (
                <div
                  className="text-center py-12"
                  data-testid="form-success"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-2">Request Received!</h3>
                  <p className="text-[#A1A1AA] mb-6">
                    Thank you for contacting Tibs Taps. We'll be in touch within 2 business hours.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-heading text-xl text-white mb-8">Contact Form</h3>
                  <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="name-detailed" className="text-[#A1A1AA] mb-2 block">Your Name *</Label>
                            <Input
                              id="name-detailed"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              className="input-dark"
                              placeholder=""
                              data-testid="input-name-detailed"
                            />
                          </div>
                          <div>
                            <Label htmlFor="email-detailed" className="text-[#A1A1AA] mb-2 block">Email *</Label>
                            <Input
                              id="email-detailed"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="input-dark"
                              placeholder=""
                              data-testid="input-email-detailed"
                            />
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="phone-detailed" className="text-[#A1A1AA] mb-2 block">Phone *</Label>
                            <Input
                              id="phone-detailed"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              required
                              className="input-dark"
                              placeholder=""
                              data-testid="input-phone-detailed"
                            />
                          </div>
                          <div>
                            <Label htmlFor="city-state" className="text-[#A1A1AA] mb-2 block">City & State *</Label>
                            <Input
                              id="city-state"
                              name="city_state"
                              value={formData.city_state}
                              onChange={handleInputChange}
                              required
                              className="input-dark"
                              placeholder=""
                              data-testid="input-city-state"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="business-detailed" className="text-[#A1A1AA] mb-2 block">Business Name *</Label>
                          <Input
                            id="business-detailed"
                            name="business_name"
                            value={formData.business_name}
                            onChange={handleInputChange}
                            required
                            className="input-dark"
                            placeholder=""
                            data-testid="input-business-detailed"
                          />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-[#A1A1AA] mb-2 block">Service Type</Label>
                            <Select
                              value={formData.service_type}
                              onValueChange={(value) => handleSelectChange("service_type", value)}
                            >
                              <SelectTrigger className="input-dark" data-testid="select-service">
                                <SelectValue placeholder="Select service..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cleaning">Line Cleaning</SelectItem>
                                <SelectItem value="installation">System Installation</SelectItem>
                                <SelectItem value="maintenance">Maintenance</SelectItem>
                                <SelectItem value="repair">Emergency Repair</SelectItem>
                                <SelectItem value="consultation">Consultation</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[#A1A1AA] mb-2 block">System Type</Label>
                            <Select
                              value={formData.system_type}
                              onValueChange={(value) => handleSelectChange("system_type", value)}
                            >
                              <SelectTrigger className="input-dark" data-testid="select-system-type">
                                <SelectValue placeholder="Select system..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="long_draw">Long Draw (Remote Cooler)</SelectItem>
                                <SelectItem value="short_draw">Short Draw (Behind Bar)</SelectItem>
                                <SelectItem value="kegerator">Kegerator</SelectItem>
                                <SelectItem value="not_sure">Not Sure</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="taps" className="text-[#A1A1AA] mb-2 block">Number of Taps</Label>
                            <Input
                              id="taps"
                              name="number_of_taps"
                              type="number"
                              min="1"
                              value={formData.number_of_taps}
                              onChange={handleInputChange}
                              className="input-dark"
                              placeholder=""
                              data-testid="input-taps"
                            />
                          </div>
                          <div>
                            <Label className="text-[#A1A1AA] mb-2 block">Preferred Contact Time</Label>
                            <Select
                              value={formData.preferred_contact_time}
                              onValueChange={(value) => handleSelectChange("preferred_contact_time", value)}
                            >
                              <SelectTrigger className="input-dark" data-testid="select-time">
                                <SelectValue placeholder="Select time..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                                <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                                <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                                <SelectItem value="anytime">Anytime</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-[#A1A1AA] mb-2 block">Additional Details</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            className="input-dark min-h-[100px]"
                            placeholder=""
                            data-testid="input-message"
                          />
                        </div>

                      {/* Honeypot - hidden from humans, bots will fill it */}
                      <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                        <input
                          type="text"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-gold w-full mt-6 flex items-center justify-center space-x-2 disabled:opacity-50"
                        data-testid="contact-form-submit"
                      >
                        {isSubmitting ? (
                          <span>Submitting...</span>
                        ) : (
                          <>
                            <span>Send Request</span>
                            <Send className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
